import AppIconButton from "../form/AppIconButton";
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { Box, Divider, Stack, Tab, Tabs, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import { useTranslation } from "react-i18next";
import { AppAutocomplete, AppButton, AppTextField } from "../form";
import { AppFormField, AppFormGrid } from "../layout";
import { GRID_FULL, GRID_HALF } from "../../lib/ui/formLayout";
import { AppSlideOver } from "../layout";
import DashboardDecorationView from "../DashboardDecorationView";
import DecorationBindingPanel from "../DecorationBindingPanel";
import type {
  DashboardDecorationPlacement,
  DashboardDecorationStyle,
  DataQueryExecutionResult,
} from "../../types/dashboard";
import type { DashboardQuery } from "../../types/query";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";
import { bindableFieldsForKind } from "../../utils/decorationBindingFields";
import { resolveDecorationBindings, syncTableBindingColumns } from "../../utils/resolveDecorationBindings";
import {
  parseStaticTable,
  stringifyStaticTable,
  type StaticTableData,
} from "../../utils/decorationCatalog";

type Props = {
  open: boolean;
  item: DashboardDecorationPlacement | null;
  onClose: () => void;
  onSaved: (item: DashboardDecorationPlacement) => void | Promise<void>;
  assignedQueries?: DashboardQuery[];
  dataQueries?: DataQueryExecutionResult[];
};

const BORDER_STYLES = ["solid", "dashed", "dotted", "none"] as const;
const TEXT_ALIGNS = ["left", "center", "right"] as const;
const VERTICAL_ALIGNS = ["top", "center", "bottom"] as const;
const OBJECT_FITS = ["cover", "contain", "fill"] as const;

type StringOption = { value: string; label: string };

type EditTabId = "content" | "style" | "image" | "table" | "binding" | "preview";

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
}) {
  const custom = useAppCustomStyles();
  return (
    <AppFormGrid spacing="compact" alignItems="center">
      <AppFormField size={{ xs: "auto" }}>
        <Box
          component="input"
          type="color"
          value={value || "#000000"}
          onChange={(e) => onChange(e.target.value)}
          sx={custom.components.decorationEditDialog.colorInput}
        />
      </AppFormField>
      <AppFormField size="grow">
        <AppTextField label={label} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      </AppFormField>
    </AppFormGrid>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
}: {
  label: string;
  value?: number;
  onChange: (v: number | undefined) => void;
  min?: number;
  max?: number;
}) {
  return (
    <AppTextField
      label={label}
      type="number"
      value={value ?? ""}
      inputProps={{ min, max }}
      onChange={(e) => {
        const raw = e.target.value;
        onChange(raw === "" ? undefined : Number(raw));
      }}
    />
  );
}

function TableEditor({
  data,
  onChange,
  t,
}: {
  data: StaticTableData;
  onChange: (d: StaticTableData) => void;
  t: (key: string) => string;
}) {
  const updateHeader = (idx: number, value: string) => {
    const headers = [...data.headers];
    headers[idx] = value;
    onChange({ ...data, headers });
  };

  const updateCell = (ri: number, ci: number, value: string) => {
    const rows = data.rows.map((r) => [...r]);
    while (rows.length <= ri) rows.push(data.headers.map(() => ""));
    while (rows[ri].length < data.headers.length) rows[ri].push("");
    rows[ri][ci] = value;
    onChange({ ...data, rows });
  };

  const addColumn = () => onChange({ headers: [...data.headers, ""], rows: data.rows.map((r) => [...r, ""]) });
  const removeColumn = (ci: number) => {
    onChange({
      headers: data.headers.filter((_, i) => i !== ci),
      rows: data.rows.map((r) => r.filter((_, i) => i !== ci)),
    });
  };
  const addRow = () => onChange({ ...data, rows: [...data.rows, data.headers.map(() => "")] });
  const removeRow = (ri: number) => onChange({ ...data, rows: data.rows.filter((_, i) => i !== ri) });

  const custom = useAppCustomStyles();
  const tableStyles = custom.components.decorationEditDialog;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={tableStyles.tableEditorHeader}>
        <Typography variant="subtitle2" fontWeight={700}>
          {t("dashboard.decor.tableEditor")}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <AppIconButton size="small" onClick={addColumn} title={t("dashboard.decor.addColumn")}>
            <AddIcon fontSize="small" />
          </AppIconButton>
        </Stack>
      </Stack>
      <Box sx={tableStyles.tableScrollBox}>
        <AppFormGrid spacing="compact" sx={tableStyles.tableRowGrid}>
          {data.headers.map((h, ci) => (
            <AppFormField key={ci} size={{ xs: 12, sm: "grow" }} sx={tableStyles.tableColumnField}>
              <Stack direction="row" spacing={0.25}>
                <AppTextField
                  size="small"
                  value={h}
                  onChange={(e) => updateHeader(ci, e.target.value)}
                  placeholder={t("dashboard.decor.column")}
                />
                {data.headers.length > 1 && (
                  <AppIconButton
                    size="small"
                    color="error"
                    confirmDelete={{ itemName: h || String(ci + 1) }}
                    onClick={() => removeColumn(ci)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </AppIconButton>
                )}
              </Stack>
            </AppFormField>
          ))}
        </AppFormGrid>
        {data.rows.map((row, ri) => (
          <AppFormGrid key={ri} spacing="compact" sx={tableStyles.tableRowGrid}>
            {data.headers.map((_, ci) => (
              <AppFormField key={ci} size={{ xs: 12, sm: "grow" }} sx={tableStyles.tableColumnField}>
                <AppTextField
                  size="small"
                  value={row[ci] ?? ""}
                  onChange={(e) => updateCell(ri, ci, e.target.value)}
                />
              </AppFormField>
            ))}
            <AppFormField size={{ xs: "auto" }}>
              <AppIconButton
                size="small"
                color="error"
                confirmDelete={{ itemName: String(ri + 1) }}
                onClick={() => removeRow(ri)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </AppIconButton>
            </AppFormField>
          </AppFormGrid>
        ))}
        <AppFormField size={GRID_FULL}>
          <AppTextField
            size="small"
            placeholder={t("dashboard.decor.addRow")}
            onFocus={(e) => {
              e.target.blur();
              addRow();
            }}
            sx={tableStyles.addRowField}
          />
        </AppFormField>
      </Box>
    </Box>
  );
}

export default function DecorationEditDialog({
  open,
  item,
  onClose,
  onSaved,
  assignedQueries = [],
  dataQueries = [],
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.decorationEditDialog;
  const [draft, setDraft] = useState<DashboardDecorationPlacement | null>(null);
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && item) {
      setDraft({ ...item, style: { ...item.style } });
      setTab(0);
    }
  }, [open, item]);

  const patchStyle = (patch: Partial<DashboardDecorationStyle>) => {
    setDraft((d) => (d ? { ...d, style: { ...d.style, ...patch } } : d));
  };

  const tableData = useMemo(
    () => (draft?.kind === "table" ? parseStaticTable(draft.content) : null),
    [draft]
  );

  const textAlignOptions = useMemo<StringOption[]>(
    () => TEXT_ALIGNS.map((a) => ({ value: a, label: t(`dashboard.decor.align.${a}`) })),
    [t]
  );
  const verticalAlignOptions = useMemo<StringOption[]>(
    () => VERTICAL_ALIGNS.map((a) => ({ value: a, label: t(`dashboard.decor.vertical.${a}`) })),
    [t]
  );
  const borderStyleOptions = useMemo<StringOption[]>(
    () => BORDER_STYLES.map((s) => ({ value: s, label: t(`dashboard.decor.border.${s}`) })),
    [t]
  );
  const objectFitOptions = useMemo<StringOption[]>(
    () => OBJECT_FITS.map((f) => ({ value: f, label: t(`dashboard.decor.fit.${f}`) })),
    [t]
  );

  const showBinding =
    draft ? bindableFieldsForKind(draft.kind).length > 0 || draft.kind === "table" : false;
  const previewItem = useMemo(
    () => (draft ? resolveDecorationBindings(draft, dataQueries) : null),
    [draft, dataQueries]
  );

  const showTitle = draft && ["heading", "label", "panel", "table", "image"].includes(draft.kind);
  const showContent = draft && ["panel", "text"].includes(draft.kind);
  const showTypography = draft && draft.kind !== "spacer" && draft.kind !== "divider";
  const showVerticalAlign = draft && ["label", "heading", "text", "panel"].includes(draft.kind);
  const showBorder = draft && !["spacer"].includes(draft.kind);
  const showImage = draft?.kind === "image";

  const editTabs = useMemo((): { id: EditTabId; label: string; icon: ReactElement }[] => {
    const tabs: { id: EditTabId; label: string; icon: ReactElement }[] = [
      { id: "content", label: t("dashboard.decor.tabContent"), icon: <NotesOutlinedIcon fontSize="small" /> },
      { id: "style", label: t("dashboard.decor.tabStyle"), icon: <PaletteOutlinedIcon fontSize="small" /> },
    ];
    if (showImage) tabs.push({ id: "image", label: t("dashboard.decor.tabImage"), icon: <ImageOutlinedIcon fontSize="small" /> });
    if (draft?.kind === "table") tabs.push({ id: "table", label: t("dashboard.decor.tabTable"), icon: <TableChartOutlinedIcon fontSize="small" /> });
    if (showBinding) tabs.push({ id: "binding", label: t("dashboard.binding.tab"), icon: <LinkOutlinedIcon fontSize="small" /> });
    tabs.push({ id: "preview", label: t("dashboard.decor.tabPreview"), icon: <PreviewOutlinedIcon fontSize="small" /> });
    return tabs;
  }, [draft?.kind, showBinding, showImage, t]);

  const activeTabId = editTabs[tab]?.id ?? "content";

  const handleSave = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await onSaved(draft);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  return (
    <AppSlideOver
      open={open}
      onClose={onClose}
      size="xl"
      defaultFullscreen
      title={t("dashboard.decor.editTitle")}
      actions={
        <>
          <AppButton variant="outlined" color="primary" onClick={onClose}>
            {t("common.cancel")}
          </AppButton>
          <AppButton variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {t("common.save")}
          </AppButton>
        </>
      }
    >
      <Box sx={styles.panelRoot}>
        <Box sx={styles.panelBody}>
          <Box sx={styles.editorPane}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={styles.tabs}>
              {editTabs.map((editTab) => (
                <Tab
                  key={editTab.id}
                  icon={editTab.icon}
                  iconPosition="start"
                  label={editTab.label}
                  sx={styles.tab}
                />
              ))}
            </Tabs>

            {activeTabId === "content" && (
                <AppFormGrid>
                  {showTitle && (
                    <AppFormField size={GRID_FULL}>
                      <AppTextField
                        label={t("dashboard.decor.titleLabel")}
                        value={draft.title || ""}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        multiline={draft.kind === "panel"}
                      />
                    </AppFormField>
                  )}
                  {showContent && (
                    <AppFormField size={GRID_FULL}>
                      <AppTextField
                        label={t("dashboard.decor.contentLabel")}
                        value={draft.content || ""}
                        onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                        multiline
                        minRows={4}
                      />
                    </AppFormField>
                  )}
                  {draft.kind === "heading" && !showContent && (
                    <AppFormField size={GRID_FULL}>
                      <AppTextField
                        label={t("dashboard.decor.contentLabel")}
                        value={draft.title || ""}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      />
                    </AppFormField>
                  )}
                  {draft.kind === "label" && (
                    <AppFormField size={GRID_FULL}>
                      <AppTextField
                        label={t("dashboard.decor.contentLabel")}
                        value={draft.title || ""}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                      />
                    </AppFormField>
                  )}
                  {draft.kind === "divider" && (
                    <AppFormField size={GRID_FULL}>
                      <Typography variant="body2" color="text.secondary">
                        {t("dashboard.decor.dividerHint")}
                      </Typography>
                    </AppFormField>
                  )}
                  {draft.kind === "spacer" && (
                    <AppFormField size={GRID_FULL}>
                      <Typography variant="body2" color="text.secondary">
                        {t("dashboard.decor.spacerHint")}
                      </Typography>
                    </AppFormField>
                  )}
                </AppFormGrid>
            )}

            {activeTabId === "style" && (
                <AppFormGrid>
                  {showTypography && (
                    <>
                      <AppFormField size={GRID_FULL}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {t("dashboard.decor.typography")}
                        </Typography>
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <NumberField
                          label={t("dashboard.decor.fontSize")}
                          value={draft.style?.fontSize}
                          onChange={(v) => patchStyle({ fontSize: v })}
                          min={8}
                          max={96}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <AppTextField
                          label={t("dashboard.decor.fontWeight")}
                          value={String(draft.style?.fontWeight ?? "")}
                          onChange={(e) => patchStyle({ fontWeight: e.target.value || undefined })}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_FULL}>
                        <ColorField
                          label={t("dashboard.decor.fontColor")}
                          value={draft.style?.fontColor}
                          onChange={(v) => patchStyle({ fontColor: v })}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <AppAutocomplete<StringOption>
                          label={t("dashboard.decor.textAlign")}
                          options={textAlignOptions}
                          value={
                            textAlignOptions.find((o) => o.value === (draft.style?.textAlign || "left")) ??
                            textAlignOptions[0]
                          }
                          onChange={(_, opt) =>
                            opt &&
                            patchStyle({ textAlign: opt.value as DashboardDecorationStyle["textAlign"] })
                          }
                          getOptionLabel={(o) => o.label}
                          isOptionEqualToValue={(a, b) => a.value === b.value}
                          disableClearable
                        />
                      </AppFormField>
                      {showVerticalAlign && (
                        <AppFormField size={GRID_HALF}>
                          <AppAutocomplete<StringOption>
                            label={t("dashboard.decor.verticalAlign")}
                            options={verticalAlignOptions}
                            value={
                              verticalAlignOptions.find(
                                (o) => o.value === (draft.style?.verticalAlign || "center")
                              ) ?? verticalAlignOptions[1]
                            }
                            onChange={(_, opt) =>
                              opt &&
                              patchStyle({
                                verticalAlign: opt.value as DashboardDecorationStyle["verticalAlign"],
                              })
                            }
                            getOptionLabel={(o) => o.label}
                            isOptionEqualToValue={(a, b) => a.value === b.value}
                            disableClearable
                          />
                        </AppFormField>
                      )}
                    </>
                  )}
                  {showBorder && (
                    <>
                      <AppFormField size={GRID_FULL}>
                        <Divider />
                      </AppFormField>
                      <AppFormField size={GRID_FULL}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {t("dashboard.decor.borderFill")}
                        </Typography>
                      </AppFormField>
                      <AppFormField size={GRID_FULL}>
                        <ColorField
                          label={t("dashboard.decor.backgroundColor")}
                          value={draft.style?.backgroundColor}
                          onChange={(v) => patchStyle({ backgroundColor: v })}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <ColorField
                          label={t("dashboard.decor.borderColor")}
                          value={draft.style?.borderColor}
                          onChange={(v) => patchStyle({ borderColor: v })}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <NumberField
                          label={t("dashboard.decor.borderWidth")}
                          value={draft.style?.borderWidth}
                          onChange={(v) => patchStyle({ borderWidth: v })}
                          min={0}
                          max={12}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <AppAutocomplete<StringOption>
                          label={t("dashboard.decor.borderStyle")}
                          options={borderStyleOptions}
                          value={
                            borderStyleOptions.find((o) => o.value === (draft.style?.borderStyle || "solid")) ??
                            borderStyleOptions[0]
                          }
                          onChange={(_, opt) =>
                            opt &&
                            patchStyle({ borderStyle: opt.value as DashboardDecorationStyle["borderStyle"] })
                          }
                          getOptionLabel={(o) => o.label}
                          isOptionEqualToValue={(a, b) => a.value === b.value}
                          disableClearable
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <NumberField
                          label={t("dashboard.decor.borderRadius")}
                          value={draft.style?.borderRadius}
                          onChange={(v) => patchStyle({ borderRadius: v })}
                          min={0}
                          max={48}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <NumberField
                          label={t("dashboard.decor.padding")}
                          value={draft.style?.padding}
                          onChange={(v) => patchStyle({ padding: v })}
                          min={0}
                          max={64}
                        />
                      </AppFormField>
                      <AppFormField size={GRID_HALF}>
                        <NumberField
                          label={t("dashboard.decor.opacity")}
                          value={draft.style?.opacity != null ? Math.round(draft.style.opacity * 100) : undefined}
                          onChange={(v) => patchStyle({ opacity: v != null ? v / 100 : undefined })}
                          min={0}
                          max={100}
                        />
                      </AppFormField>
                      {draft.kind === "divider" && (
                        <AppFormField size={GRID_FULL}>
                          <NumberField
                            label={t("dashboard.decor.dividerThickness")}
                            value={draft.style?.dividerThickness}
                            onChange={(v) => patchStyle({ dividerThickness: v })}
                            min={1}
                            max={12}
                          />
                        </AppFormField>
                      )}
                    </>
                  )}
                </AppFormGrid>
            )}

            {activeTabId === "image" && showImage && (
                <AppFormGrid>
                  <AppFormField size={GRID_FULL}>
                    <AppTextField
                      label={t("dashboard.decor.imageUrl")}
                      value={draft.style?.imageUrl || ""}
                      onChange={(e) => patchStyle({ imageUrl: e.target.value })}
                      placeholder="https://…"
                    />
                  </AppFormField>
                  <AppFormField size={GRID_FULL}>
                    <AppAutocomplete<StringOption>
                      label={t("dashboard.decor.objectFit")}
                      options={objectFitOptions}
                      value={
                        objectFitOptions.find((o) => o.value === (draft.style?.objectFit || "cover")) ??
                        objectFitOptions[0]
                      }
                      onChange={(_, opt) =>
                        opt &&
                        patchStyle({ objectFit: opt.value as DashboardDecorationStyle["objectFit"] })
                      }
                      getOptionLabel={(o) => o.label}
                      isOptionEqualToValue={(a, b) => a.value === b.value}
                      disableClearable
                    />
                  </AppFormField>
                </AppFormGrid>
            )}

            {activeTabId === "table" && draft.kind === "table" && tableData && (
                <TableEditor
                  data={tableData}
                  t={t}
                  onChange={(d) =>
                    setDraft((prev) => {
                      if (!prev) return prev;
                      const next = { ...prev, content: stringifyStaticTable(d) };
                      const syncedTableBinding = syncTableBindingColumns(next);
                      return syncedTableBinding
                        ? { ...next, tableBinding: syncedTableBinding }
                        : next;
                    })
                  }
                />
            )}

            {activeTabId === "binding" && showBinding && (
                <DecorationBindingPanel
                  draft={draft}
                  assignedQueries={assignedQueries}
                  dataQueries={dataQueries}
                  onChange={setDraft}
                />
            )}

            {activeTabId === "preview" && (
              <Box sx={styles.previewTabContainer}>
                <Box sx={styles.previewTabViewport}>
                  <DashboardDecorationView item={previewItem || draft} />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </AppSlideOver>
  );
}
